import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { withChunkRetry } from '@core/utils/lazyWithRetry';

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages';
import enAdmin from './locales/en/admin.json';
import enBroker from './locales/en/broker.json';
import enBuyer from './locales/en/buyer.json';
import enCommon from './locales/en/common.json';
import enDashboard from './locales/en/dashboard.json';
import enDeal from './locales/en/deal.json';
import enDocument from './locales/en/document.json';
import enEnquiry from './locales/en/enquiry.json';
import enForm from './locales/en/form.json';
import enLawyer from './locales/en/lawyer.json';
import enNav from './locales/en/nav.json';
import enOwnership from './locales/en/ownership.json';
import enProperty from './locales/en/property.json';
import enPublic from './locales/en/public.json';
import enSeller from './locales/en/seller.json';
import enStatus from './locales/en/status.json';
import enTable from './locales/en/table.json';
import enToast from './locales/en/toast.json';

/**
 * One namespace, `common`, nested by surface. The locale files are split by surface for
 * editing only — every key path stays `nav.dashboard`, so splitting a file never moves a key.
 */
export const DEFAULT_NAMESPACE = 'common';

const en = {
  ...enNav,
  ...enCommon,
  ...enDashboard,
  ...enStatus,
  ...enForm,
  ...enTable,
  ...enProperty,
  ...enDocument,
  ...enOwnership,
  ...enEnquiry,
  ...enDeal,
  ...enLawyer,
  ...enAdmin,
  ...enBuyer,
  ...enBroker,
  ...enSeller,
  ...enPublic,
  ...enToast
};

/**
 * A global instance and no provider: `useTranslation()` then needs no wrapper, and a
 * component test asserts the real English string instead of a key.
 *
 * The init is guarded because vitest imports this module from `src/test/setup.ts` for
 * every test file — re-initialising throws on the second pass.
 */
if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: {
      en: { [DEFAULT_NAMESPACE]: en }
    },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE],
    // React already escapes everything it renders; escaping here double-encodes apostrophes.
    interpolation: { escapeValue: false },
    returnNull: false
  });
}

/** `ta` is ~54 KB gzip and only loaded once a user actually switches to it. */
export async function ensureTamilBundleLoaded(): Promise<void> {
  if (i18n.hasResourceBundle('ta', DEFAULT_NAMESPACE)) return;
  const { default: ta } = await withChunkRetry(() => import('./taLocale'));
  i18n.addResourceBundle('ta', DEFAULT_NAMESPACE, ta, true, true);
}

export default i18n;
